import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Trophy, Crown, MedalMilitary } from '@phosphor-icons/react';

const COMP_COLORS = {
  league: { bg: '#FF3B30', label: 'EWFL League' },
  cup: { bg: '#FFD700', label: 'Cup' },
  championsLeague: { bg: '#CCFF00', label: 'Asian Cup' },
  asianCup: { bg: '#CCFF00', label: 'Asian Cup' },
  cwc: { bg: '#007AFF', label: 'Club World Cup' }
};

export const TrophyHistoryPage = ({ gameState }) => {
  const navigate = useNavigate();
  const history = gameState?.trophyHistory || [];

  const byTeam = useMemo(() => {
    const map = {};
    history.forEach(h => {
      if (!map[h.winnerName]) map[h.winnerName] = { name: h.winnerName, total: 0, byComp: {} };
      map[h.winnerName].total += 1;
      map[h.winnerName].byComp[h.competition] = (map[h.winnerName].byComp[h.competition] || 0) + 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [history]);

  const bySeason = useMemo(() => {
    const map = {};
    history.forEach(h => {
      const key = `${h.season}-${h.year}`;
      if (!map[key]) map[key] = { season: h.season, year: h.year, items: [] };
      map[key].items.push(h);
    });
    return Object.values(map).sort((a, b) => b.season - a.season);
  }, [history]);

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-[#A0A0A5] hover:text-white" data-testid="history-back-btn"><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide flex items-center gap-3">
              <Trophy size={32} weight="fill" className="text-[#FFD700]" /> Trophy Cabinet
            </h1>
            <p className="text-[#A0A0A5] mt-1">Every champion across every competition, every season</p>
          </div>
        </div>

        {history.length === 0 ? (
          <div className="card-surface rounded-sm p-12 text-center">
            <MedalMilitary size={64} weight="fill" className="text-[#6E6E73] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white uppercase mb-2">No Trophies Yet</h2>
            <p className="text-[#A0A0A5]">Complete a league season, cup, or tournament to record winners here.</p>
          </div>
        ) : (
          <Tabs defaultValue="seasons" className="w-full">
            <TabsList className="bg-[#121216] border border-[#2C2C35] w-full grid grid-cols-2 mb-6">
              <TabsTrigger value="seasons" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black font-heading uppercase text-sm" data-testid="history-tab-seasons">By Season</TabsTrigger>
              <TabsTrigger value="teams" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-heading uppercase text-sm" data-testid="history-tab-teams">By Team</TabsTrigger>
            </TabsList>

            <TabsContent value="seasons">
              <ScrollArea className="h-[600px]">
                <div className="space-y-5">
                  {bySeason.map(s => (
                    <div key={`${s.season}-${s.year}`} className="card-surface rounded-sm">
                      <div className="border-b border-[#2C2C35] px-4 py-3">
                        <h3 className="font-heading font-bold text-white uppercase tracking-wide">Season {s.season} ({s.year})</h3>
                      </div>
                      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {s.items.map((h, i) => {
                          const meta = COMP_COLORS[h.competition] || { bg: '#A0A0A5', label: h.competitionLabel };
                          return (
                            <div key={i} className="card-elevated rounded-sm p-4 border-t-2" style={{ borderTopColor: meta.bg }} data-testid={`trophy-${h.competition}-${s.season}`}>
                              <Crown size={24} weight="fill" style={{ color: meta.bg }} className="mx-auto mb-2" />
                              <p className="text-xs font-bold uppercase tracking-wider text-center" style={{ color: meta.bg }}>{meta.label}</p>
                              <p className="text-center text-white font-bold mt-1 truncate">{h.winnerName}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="teams">
              <div className="card-surface rounded-sm">
                <div className="border-b border-[#2C2C35] px-4 py-3">
                  <h3 className="font-heading font-bold text-white uppercase tracking-wide">Most Decorated Teams</h3>
                </div>
                <ScrollArea className="h-[600px]">
                  {byTeam.map((t, i) => (
                    <div key={t.name} className={`flex items-center gap-3 px-4 py-3 border-b border-[#2C2C35] ${i % 2 === 0 ? 'bg-[#0A0A0C]' : 'bg-[#121216]'}`} data-testid={`team-trophies-${i}`}>
                      <span className={`font-mono font-bold text-xl w-8 ${i === 0 ? 'text-[#FFD700]' : i === 1 ? 'text-[#C0C0C0]' : i === 2 ? 'text-[#CD7F32]' : 'text-[#6E6E73]'}`}>{i + 1}</span>
                      <div className="flex-1">
                        <p className="text-white font-bold">{t.name}</p>
                        <div className="flex gap-2 mt-1 text-[10px]">
                          {Object.entries(t.byComp).map(([comp, count]) => {
                            const meta = COMP_COLORS[comp] || { bg: '#A0A0A5', label: comp };
                            return (
                              <span key={comp} className="px-2 py-0.5 rounded-sm font-mono font-bold" style={{ color: meta.bg, backgroundColor: `${meta.bg}22` }}>
                                {meta.label}: {count}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <span className="font-mono font-bold text-2xl text-[#FFD700]">{t.total}</span>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};
